'use client';
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { createClient } from '@/lib/supabase/client';
import AreaTabs from './AreaTabs';
import CafeListPanel from './CafeListPanel';
import MapPanel from './MapPanel';
import SortControls from './SortControls';
import { Search, Layers, Loader2 } from 'lucide-react';

type SortKey = 'rating' | 'price' | 'reviews';

export interface CafeMapItem {
  id: string;
  name: string;
  area: string;
  address: string;
  lat: number;
  lng: number;
  rating: number;
  reviewCount: number;
  priceMin: number;
  priceMax: number;
  isOpen: boolean;
  photos: string[];
  vibeTags: string[];
  wifi: boolean;
  outlet: boolean;
  parking: boolean;
  noiseLevel: string;
  priceRange: string;
  imageUrl: string;
}

export default function JelajahiClient() {
  const supabase = createClient();
  const [cafes, setCafes] = useState<CafeMapItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedArea, setSelectedArea] = useState('Semua');
  const [sortBy, setSortBy] = useState<SortKey>('rating');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCafeId, setSelectedCafeId] = useState<string | null>(null);
  const [showMap, setShowMap] = useState(true);
  const [mapVisibleIds, setMapVisibleIds] = useState<string[] | null>(null);
  const [filterByMap, setFilterByMap] = useState(false);

  useEffect(() => {
    const fetchCafes = async () => {
      const { data, error } = await supabase
        .from('coffee_shops')
        .select('id, name, area, address, latitude, longitude, rating, review_count, price_range, is_open, image_url, tags, has_wifi, has_outlets, has_parking, crowd_level')
        .order('rating', { ascending: false });

      if (error) {
        console.log('Jelajahi fetch error:', error.message);
        setLoading(false);
        return;
      }

      const mapped: CafeMapItem[] = (data || []).map((c: any) => {
        const priceStr = c.price_range || 'Rp 20.000 - 40.000';
        const parts = priceStr.replace(/Rp\s*/g, '').split('-').map((s: string) => parseInt(s.replace(/\./g, '').trim()) || 0);
        return {
          id: c.id,
          name: c.name,
          area: c.area || 'Malang',
          address: c.address || '',
          lat: c.latitude || -7.9666,
          lng: c.longitude || 112.6326,
          rating: c.rating || 0,
          reviewCount: c.review_count || 0,
          priceMin: parts[0] || 20000,
          priceMax: parts[1] || 40000,
          isOpen: c.is_open ?? true,
          photos: c.image_url ? [c.image_url] : [],
          vibeTags: c.tags || [],
          wifi: c.has_wifi || false,
          outlet: c.has_outlets || false,
          parking: c.has_parking || false,
          noiseLevel: c.crowd_level || 'Sedang',
          priceRange: c.price_range || 'Rp 20.000 - 40.000',
          imageUrl: c.image_url || '',
        };
      });

      setCafes(mapped);
      setLoading(false);
    };

    fetchCafes();
  }, []);

  const handleBoundsChange = useCallback((visibleIds: string[]) => {
    setMapVisibleIds(visibleIds);
  }, []);

  const filteredCafes = useMemo(() => {
    let result = [...cafes];

    if (selectedArea !== 'Semua') {
      result = result.filter((c) => c.area === selectedArea);
    }

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(
        (c) =>
          c.name.toLowerCase().includes(q) ||
          c.area.toLowerCase().includes(q) ||
          c.vibeTags.some((t) => t.toLowerCase().includes(q))
      );
    }

    if (filterByMap && mapVisibleIds !== null) {
      result = result.filter((c) => mapVisibleIds.includes(c.id));
    }

    result.sort((a, b) => {
      if (sortBy === 'rating') return b.rating - a.rating;
      if (sortBy === 'price') return a.priceMin - b.priceMin;
      if (sortBy === 'reviews') return b.reviewCount - a.reviewCount;
      return 0;
    });

    return result;
  }, [cafes, selectedArea, sortBy, searchQuery, filterByMap, mapVisibleIds]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <Loader2 size={32} className="animate-spin text-accent mx-auto mb-3" />
          <p className="text-sm text-muted-foreground">Memuat coffeeshop Malang...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-screen lg:h-auto">
      {/* Header */}
      <div className="px-4 lg:px-8 xl:px-10 pt-6 pb-3 bg-background border-b border-border sticky top-0 z-20">
        <div className="max-w-screen-2xl mx-auto">
          <h1 className="text-xl font-800 text-primary mb-3">🗺️ Jelajahi Malang</h1>
          <div className="flex gap-3 mb-3">
            <div className="relative flex-1">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Cari nama cafe atau area..."
                className="input-field pl-9 h-10 text-sm"
              />
            </div>
            <button
              onClick={() => setShowMap(!showMap)}
              className={`lg:hidden px-4 py-2 rounded-xl border text-sm font-600 transition-colors ${showMap ? 'bg-accent text-white border-accent' : 'bg-card border-border text-primary'}`}
            >
              {showMap ? '📋 List' : '🗺️ Peta'}
            </button>
          </div>
          <div className="flex items-center justify-between gap-3">
            <AreaTabs selected={selectedArea} onChange={setSelectedArea} />
            <button
              onClick={() => setFilterByMap((v) => !v)}
              title={filterByMap ? 'Tampilkan semua cafe' : 'Filter list sesuai area peta'}
              className={`hidden lg:flex flex-shrink-0 items-center gap-1.5 px-3 py-1.5 rounded-xl border text-xs font-600 transition-all ${
                filterByMap
                  ? 'bg-accent text-white border-accent shadow-sm'
                  : 'bg-card border-border text-muted-foreground hover:border-accent hover:text-accent'
              }`}
            >
              <Layers size={13} />
              {filterByMap ? 'Area Peta ✓' : 'Filter Peta'}
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 max-w-screen-2xl mx-auto w-full px-4 lg:px-8 xl:px-10 2xl:px-16">
        <div className="lg:grid lg:grid-cols-5 lg:gap-6 lg:h-[calc(100vh-200px)]">
          {/* List Panel */}
          <div className={`lg:col-span-2 ${showMap ? 'hidden lg:block' : 'block'} overflow-y-auto pt-4`}>
            <div className="flex items-center justify-between mb-1">
              <SortControls sortBy={sortBy} onChange={setSortBy} count={filteredCafes.length} />
              {!showMap && (
                <button
                  onClick={() => setFilterByMap((v) => !v)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl border text-xs font-600 transition-all lg:hidden ${
                    filterByMap
                      ? 'bg-accent text-white border-accent' :'bg-card border-border text-muted-foreground'
                  }`}
                >
                  <Layers size={12} />
                  {filterByMap ? 'Area Peta ✓' : 'Filter Peta'}
                </button>
              )}
            </div>
            {filterByMap && mapVisibleIds !== null && (
              <div className="mb-3 flex items-center gap-2 px-3 py-2 bg-accent/10 rounded-xl border border-accent/20">
                <Layers size={13} className="text-accent flex-shrink-0" />
                <p className="text-xs text-accent font-600">
                  Menampilkan {filteredCafes.length} cafe di area peta yang terlihat
                </p>
                <button
                  onClick={() => setFilterByMap(false)}
                  className="ml-auto text-xs text-muted-foreground hover:text-accent transition-colors font-500"
                >
                  Reset
                </button>
              </div>
            )}
            <CafeListPanel
              cafes={filteredCafes}
              selectedId={selectedCafeId}
              onSelect={setSelectedCafeId}
            />
          </div>

          {/* Map Panel */}
          <div className={`lg:col-span-3 ${showMap ? 'block' : 'hidden lg:block'} sticky top-[200px] h-[calc(100vh-240px)] mt-4`}>
            <MapPanel
              cafes={filteredCafes}
              selectedId={selectedCafeId}
              onSelect={setSelectedCafeId}
              onBoundsChange={handleBoundsChange}
            />
          </div>
        </div>
      </div>
    </div>
  );
}